/**
 * Private Bilder in der Leseansicht (#5235).
 *
 * Ein privates Storage-Bild lässt sich ohne Signatur nicht laden. Scheitert
 * ein Bild im Lesetext, das auf den Storage zeigt, holt dieser Hook für den
 * Beitrag signierte Adressen und setzt sie ein. content-api signiert nur
 * Bilder, die an den Beitrag gebunden sind, und nur für angemeldete Leser.
 *
 * Die Adressen gelten wenige Minuten. Sie werden hier bis kurz vor Ablauf
 * gemerkt, weil der Lesetext beim Neu-Rendern die ursprünglichen Adressen
 * zurückbekommt und die Bilder dann erneut scheitern.
 */
import { useEffect } from 'react'
import { getAccessToken } from '../auth'
import { STORAGE_MEDIA_RE, signedMediaUrls } from '../api/postMedia'

const FREE_PARAMS = ['variant', 'width', 'height', 'format', 'v', 'quality']

function withFreeParams(signed: string, original: string): string {
  try {
    const out = new URL(signed)
    const src = new URL(original)
    for (const key of FREE_PARAMS) {
      const value = src.searchParams.get(key)
      if (value !== null && !out.searchParams.has(key)) out.searchParams.set(key, value)
    }
    return out.toString()
  } catch {
    return signed
  }
}

export function useSignedMedia(postId: number | null | undefined, root: HTMLElement | null): void {
  useEffect(() => {
    if (!postId || !root || !STORAGE_MEDIA_RE || !getAccessToken()) return
    const pattern = STORAGE_MEDIA_RE
    const cache = new Map<number, { url: string; until: number }>()
    const refused = new Set<number>()
    const waiting = new Map<number, HTMLImageElement[]>()
    let timer: number | null = null
    let alive = true

    const idOf = (img: HTMLImageElement): number | null => {
      const original = img.dataset.unsignedSrc || img.getAttribute('src') || ''
      const match = pattern.exec(original)
      return match ? Number(match[1]) : null
    }

    const apply = (img: HTMLImageElement, url: string) => {
      const original = img.dataset.unsignedSrc || img.getAttribute('src') || ''
      img.dataset.unsignedSrc = original
      img.dataset.signed = '1'
      img.src = withFreeParams(url, original)
    }

    const flush = async () => {
      timer = null
      const ids = [...waiting.keys()]
      if (ids.length === 0) return
      const batch = new Map(waiting)
      waiting.clear()
      const { urls, ttl } = await signedMediaUrls(postId, ids)
      if (!alive) return
      const until = Date.now() + Math.max(30, (ttl || 300) - 60) * 1000
      for (const [id, imgs] of batch) {
        const url = urls[String(id)]
        if (!url) { refused.add(id); continue }
        cache.set(id, { url, until })
        imgs.forEach((img) => { if (img.isConnected) apply(img, url) })
      }
    }

    const handle = (img: HTMLImageElement) => {
      // Ein signiertes Bild, das trotzdem scheitert, nicht endlos erneut holen.
      if (img.dataset.signed === '1' && img.src.includes('sig=')) {
        const id = idOf(img)
        if (id !== null && (cache.get(id)?.until ?? 0) > Date.now()) return
        delete img.dataset.signed
      }
      const id = idOf(img)
      if (id === null || refused.has(id)) return
      const hit = cache.get(id)
      if (hit && hit.until > Date.now()) { apply(img, hit.url); return }
      const list = waiting.get(id) || []
      list.push(img)
      waiting.set(id, list)
      if (timer === null) timer = window.setTimeout(() => void flush(), 60)
    }

    const onError = (ev: Event) => {
      if (ev.target instanceof HTMLImageElement) handle(ev.target)
    }
    // `error` steigt nicht auf, deshalb in der Fangphase am Wurzelknoten.
    root.addEventListener('error', onError, true)
    // Bilder, die schon vor dem Anhängen gescheitert sind.
    root.querySelectorAll('img').forEach((img) => {
      if (img.complete && img.naturalWidth === 0) handle(img)
    })
    return () => {
      alive = false
      root.removeEventListener('error', onError, true)
      if (timer !== null) window.clearTimeout(timer)
    }
  }, [postId, root])
}
